import { memo, useState, useEffect, useRef } from "react";
import { Container, Grid2, Typography, Box, Stack } from "@mui/material";
import ProfileWidget from "../components/ProfileWidget";
import ChatWidget from "../components/ChatWidget";
import { useQuery } from "@apollo/client";
import { FETCH_USER_PROFILE } from "@company/graphql/queries/fetchUserProfile";
import VideoWidget from "@dashboard/components/VideoWidget";
import UserGuidelineWidget from "@dashboard/components/UserGuidelineWidget";
import { EmailVerificationBanner } from "@auth/components/EmailVerificationBanner";
import CheckoutContinuationBanner from "@dashboard/components/CheckoutContinuationBanner";
import OnboardingContinuationBanner from "@dashboard/components/OnboardingContinuationBanner";
import { NotificationModal } from "../../../ui-kit/NotificationModal";
import { 
  useUserNotifications, 
  useMarkNotificationRead, 
  useAcknowledgeNotification, 
  useDismissNotification 
} from "../../../hooks/useNotifications";

const DashboardPage = memo(() => {
    const { data } = useQuery(FETCH_USER_PROFILE);
    
    // Notification modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [modalError, setModalError] = useState<string | null>(null);
    const hasMarkedAsRead = useRef(false);
    
    // Fetch latest notifications (including dismissed ones that might need reshowing)
    const { data: notificationData, refetch: refetchNotifications } = useUserNotifications({
        limit: 10, // Get more notifications to filter through dismissed ones
        unreadOnly: false // Get all notifications, we'll filter on client side
    });
    
    // Notification mutations
    const [markAsRead, { loading: markingAsRead }] = useMarkNotificationRead();
    const [acknowledge, { loading: acknowledging }] = useAcknowledgeNotification();
    const [dismiss, { loading: dismissing }] = useDismissNotification();
    
    // Filter notifications to find the latest one that should be shown
    // Show dismissed notifications again after 20 minutes
    const getLatestShowableNotification = () => {
        const notifications = notificationData?.userNotifications?.nodes || [];
        const now = new Date();
        const twentyMinutesAgo = new Date(now.getTime() - 20 * 60 * 1000); // 20 minutes ago
        
        // Sort by creation date (newest first)
        const sortedNotifications = [...notifications].sort((a, b) => 
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        
        for (const notification of sortedNotifications) {
            // Skip if already acknowledged
            if (notification.changesSummary?.isAcknowledged) {
                continue;
            }
            
            // Show if never dismissed and not read
            if (!notification.changesSummary?.isDismissed && !notification.isRead) {
                return notification;
            }
            
            // Show if dismissed more than 20 minutes ago
            const dismissedAt = notification.changesSummary?.dismissedAt;
            if (dismissedAt && new Date(dismissedAt) < twentyMinutesAgo) {
                return notification;
            }
        }
        
        return null;
    };
    
    const latestNotification = getLatestShowableNotification();
    
    // Auto-open modal when there's an unread notification
    useEffect(() => {
        if (latestNotification && !modalOpen && !hasMarkedAsRead.current) {
            setModalOpen(true);
            hasMarkedAsRead.current = true;
            
            // DO NOT auto-mark as read - user must acknowledge first!
            // The notification will only be marked as read when user clicks "Verstanden"
        }
    }, [latestNotification, modalOpen]);
    
    // Reset flag when notifications change (new notification available)
    useEffect(() => {
        hasMarkedAsRead.current = false;
    }, [notificationData?.userNotifications?.totalCount]);
    
    const handleModalClose = async () => {
        if (!latestNotification) return;
        
        try {
            // Mark as dismissed when closing without acknowledgment
            // Note: Notification remains unread until user acknowledges it
            await dismiss({
                variables: { input: { notificationId: latestNotification.id } }
            });
            setModalOpen(false);
            setModalError(null);
            
            // Refetch notifications to update the UI
            refetchNotifications();
        } catch (error) {
            console.error('Failed to dismiss notification:', error);
            setModalError('Fehler beim Schließen der Benachrichtigung');
        }
    };
    
    const handleAcknowledge = async () => {
        if (!latestNotification) return;
        
        try {
            // First mark as read, then acknowledge
            await markAsRead({
                variables: { input: { notificationId: latestNotification.id } }
            });
            
            await acknowledge({
                variables: { input: { notificationId: latestNotification.id } }
            });
            
            setModalOpen(false);
            setModalError(null);
            
            // Refetch notifications to update the UI
            refetchNotifications();
        } catch (error) {
            console.error('Failed to acknowledge notification:', error);
            setModalError('Fehler beim Bestätigen der Benachrichtigung');
        }
    };

    // Extracted main content as a nested component to keep render section clean
    const Content = () => (
        <Stack spacing={2} width="100%">
            {/* Show verification banner if email is not verified */}
            {data?.userProfile && !data.userProfile.emailVerified && (
                <EmailVerificationBanner userEmail={data.userProfile.email} />
            )}

            {/* Show checkout continuation banner if user has saved checkout state */}
            <CheckoutContinuationBanner />

            {/* Show onboarding continuation banner if user has onboarding draft */}
            <OnboardingContinuationBanner />

            <Grid2 container size={12} spacing={3}>
                <Grid2 size={{ xs: 12, sm: 4, lg: 4 }}>
                    <ProfileWidget />
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 8, lg: "grow" }}>
                    <UserGuidelineWidget />
                </Grid2>
            </Grid2>

            {/* Video & Chat widgets */}
            <Grid2 container size={12} columnSpacing={3} maxHeight={600}>
                <Grid2 size={{ xs: 12, sm: 8 }} maxHeight={"inherit"}>
                    <VideoWidget />
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 4 }} maxHeight={"inherit"}>
                    <ChatWidget />
                </Grid2>
            </Grid2>
        </Stack>
    );

    return (
        <Container maxWidth="lg" sx={{ py: 1 }}>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h3" fontFamily="Montserrat">
                    {`Hey, ${data?.userProfile?.name}!`}
                </Typography>
            </Box>

            {/* Main dashboard content */}
            <Content />
            
            {/* Notification Modal */}
            <NotificationModal
                open={modalOpen}
                notification={latestNotification}
                onClose={handleModalClose}
                onAcknowledge={handleAcknowledge}
                loading={acknowledging || dismissing}
                error={modalError}
            />
        </Container>
    );
});

export default DashboardPage;